'use client';

import { useRouter } from 'next/navigation';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import type { GameV1 } from '@/api/games';
import { Button } from '@/components/buttons';
import { IconComponent } from '@/components/IconComponent';
import { ImageComponent } from '@/components/Image';
import { getGameCover } from '@/helpers/cdn';
import { addQueryParameters, capitalizeWords } from '@/helpers/common';
import { Modals } from '@/helpers/modals';
import { useGameOpenFlow, useGameProviders } from '@/hooks/game';
import { useModalsStore } from '@/store';
import {
  ScNotAuthButtons,
  ScNotAuthCard,
  ScNotAuthCover,
  ScNotAuthTitleRow,
} from './styled';

interface IGameErrorNotAuthMobileProps {
  game: GameV1;
  isDemoAvailable: boolean;
}

export const GameErrorNotAuthMobile: FC<IGameErrorNotAuthMobileProps> = ({
  game,
  isDemoAvailable,
}) => {
  const { t } = useTranslation('common');
  const open = useModalsStore.use.open();
  const { push } = useRouter();
  const { closeGameFlow } = useGameOpenFlow();

  const { currentProvider } = useGameProviders({ providerId: game.provider });

  const gameImageAlt = t('game.imageAlt', {
    gameName: game.title,
    providerName: currentProvider?.title ?? game.provider,
  });

  return (
    <ScNotAuthCard>
      <ScNotAuthTitleRow>
        <button
          aria-label={t('auth.registration.buttons.back')}
          className="back"
          onClick={closeGameFlow}
          type="button"
        >
          <IconComponent name="whiteArrowRight" size="24px" />
        </button>
        <h1>{capitalizeWords(game.title)}</h1>
        <span className="spacer" />
      </ScNotAuthTitleRow>
      <ScNotAuthCover>
        <ImageComponent
          alt={gameImageAlt}
          fill
          sizes="180px"
          src={getGameCover(game.id)}
          style={{ objectFit: 'cover' }}
        />
      </ScNotAuthCover>
      <ScNotAuthButtons>
        <Button onClick={() => open(Modals.Registration)} primary>
          {t('button.signUp')}
        </Button>
        {isDemoAvailable && (
          <button
            className="demo-btn"
            onClick={() => addQueryParameters(push, { demo: 'true' })}
            type="button"
          >
            {t('button.playDemo')}
          </button>
        )}
      </ScNotAuthButtons>
    </ScNotAuthCard>
  );
};
